import { arrayRemove, arrayUnion, doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { db } from "../../firebase";
import {
  buildOmiMatchState,
  canPlayCardInTrick,
  formatSuit,
  getTeamKeyFromSeat,
  resolveTrickWinner,
} from "./omiUtils";

function getNextTurn(order, currentUserId) {
  const currentIndex = order.findIndex((entry) => entry === currentUserId);
  if (currentIndex === -1) return order[0] || null;
  return order[(currentIndex + 1) % order.length] || null;
}

export default function OmiGameRoom({ room, user, onLeave }) {
  const [busy, setBusy] = useState(false);
  const players = room.players || [];
  const hands = room.hands || {};
  const myHand = hands[user?.uid] || [];
  const myTurn = room.turnUserId === user?.uid;
  const playersById = new Map(players.map((player) => [player.userId, player]));
  const currentTurnPlayer = playersById.get(room.turnUserId);
  const playableHandIds = useMemo(
    () =>
      new Set(
        myHand
          .filter((card) => canPlayCardInTrick(card, myHand, room.trick || []))
          .map((card) => card.id),
      ),
    [myHand, room.trick],
  );
  const rematchReady = room.rematchReady || [];
  const iAmReady = rematchReady.includes(user?.uid);
  const forfeitedPlayer = playersById.get(room.forfeitedBy);

  const handlePlayCard = async (cardId) => {
    if (!user?.uid || !myTurn || room.status !== "playing") return;

    const roomRef = doc(db, "omi_rooms", room.id);
    try {
      await runTransaction(db, async (transaction) => {
        const roomSnap = await transaction.get(roomRef);
        if (!roomSnap.exists()) throw new Error("Room not found.");

        const roomData = roomSnap.data() || {};
        if (roomData.status !== "playing") return;
        if (roomData.turnUserId !== user.uid) return;

        const nextHands = { ...(roomData.hands || {}) };
        const myCards = [...(nextHands[user.uid] || [])];
        const cardIndex = myCards.findIndex((card) => card.id === cardId);
        if (cardIndex === -1) throw new Error("Card not found.");

        const [playedCard] = myCards.splice(cardIndex, 1);
        if (!canPlayCardInTrick(playedCard, nextHands[user.uid] || myCards, roomData.trick || [])) {
          throw new Error("You must follow the lead suit when you can.");
        }
        nextHands[user.uid] = myCards;

        const currentTrick = [...(roomData.trick || []), { userId: user.uid, card: playedCard }];
        const turnOrder = roomData.turnOrder || players.map((player) => player.userId);

        if (currentTrick.length < 4) {
          transaction.update(roomRef, {
            hands: nextHands,
            trick: currentTrick,
            turnUserId: getNextTurn(turnOrder, user.uid),
            updatedAt: serverTimestamp(),
          });
          return;
        }

        const resolution = resolveTrickWinner(currentTrick, roomData.players || [], roomData.trump);
        const nextScores = { ...(roomData.scores || { teamA: 0, teamB: 0 }) };
        nextScores[resolution.teamKey] = Number(nextScores[resolution.teamKey] || 0) + 1;

        const finished = Object.values(nextHands).every(
          (cards) => Array.isArray(cards) && cards.length === 0,
        );

        if (finished) {
          const teamA = Number(nextScores.teamA || 0);
          const teamB = Number(nextScores.teamB || 0);
          const isDraw = teamA === teamB;
          const winnerTeam = isDraw ? "draw" : teamA > teamB ? "teamA" : "teamB";
          const pot = Number(roomData.betAmount || 0) * players.length;
          const payoutPool = Number((pot * 0.9).toFixed(2));

          if (winnerTeam === "draw") {
            for (const player of players) {
              const userRef = doc(db, "users", player.userId);
              const userSnap = await transaction.get(userRef);
              if (!userSnap.exists()) continue;
              const balance = Number(userSnap.data()?.balance || 0);
              transaction.update(userRef, {
                balance: Number((balance + Number(roomData.betAmount || 0)).toFixed(2)),
              });
            }
          } else {
            const winners = players.filter(
              (player) => getTeamKeyFromSeat(Number(player.seat || 1)) === winnerTeam,
            );
            const splitPayout = Number((payoutPool / winners.length).toFixed(2));
            for (const player of winners) {
              const userRef = doc(db, "users", player.userId);
              const userSnap = await transaction.get(userRef);
              if (!userSnap.exists()) continue;
              const balance = Number(userSnap.data()?.balance || 0);
              transaction.update(userRef, {
                balance: Number((balance + splitPayout).toFixed(2)),
              });
            }
          }

          transaction.update(roomRef, {
            hands: nextHands,
            trick: [],
            lastTrick: currentTrick,
            scores: nextScores,
            winnerTeam,
            turnUserId: resolution.userId,
            status: "finished",
            payoutProcessed: true,
            rematchReady: [],
            finishedReason: "completed",
            forfeitedBy: null,
            updatedAt: serverTimestamp(),
            finishedAt: serverTimestamp(),
          });
          return;
        }

        transaction.update(roomRef, {
          hands: nextHands,
          trick: [],
          lastTrick: currentTrick,
          scores: nextScores,
          turnUserId: resolution.userId,
          trickNumber: Number(roomData.trickNumber || 1) + 1,
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      toast.error(error?.message || "Failed to play card.");
    }
  };

  const handleRematch = async () => {
    if (!user?.uid || room.status !== "finished" || busy) return;

    setBusy(true);
    try {
      await runTransaction(db, async (transaction) => {
        const roomRef = doc(db, "omi_rooms", room.id);
        const roomSnap = await transaction.get(roomRef);
        if (!roomSnap.exists()) throw new Error("Room not found.");

        const roomData = roomSnap.data() || {};
        if (roomData.status !== "finished") return;

        const playersList = roomData.players || [];
        const me = playersList.find((player) => player.userId === user.uid);
        if (!me) throw new Error("You are no longer part of this table.");

        const currentReady = roomData.rematchReady || [];
        const alreadyReady = currentReady.includes(user.uid);

        if (alreadyReady) {
          transaction.update(roomRef, {
            rematchReady: arrayRemove(user.uid),
            updatedAt: serverTimestamp(),
          });
          return;
        }

        const nextReady = [...currentReady, user.uid];
        if (nextReady.length < playersList.length) {
          transaction.update(roomRef, {
            rematchReady: arrayUnion(user.uid),
            updatedAt: serverTimestamp(),
          });
          return;
        }

        const betAmount = Number(roomData.betAmount || 0);
        for (const player of playersList) {
          const playerRef = doc(db, "users", player.userId);
          const playerSnap = await transaction.get(playerRef);
          if (!playerSnap.exists()) {
            throw new Error("A player account is missing.");
          }
          const balance = Number(playerSnap.data()?.balance || 0);
          if (balance < betAmount) {
            throw new Error(`${player.username || "A player"} does not have enough balance for rematch.`);
          }
        }

        const shuffledPlayers = playersList.map((player) => ({ ...player }));
        const nextRoundState = {
          ...roomData,
          ...resolveNextRoundState(shuffledPlayers, Number(roomData.round || 1)),
        };

        for (const player of playersList) {
          const playerRef = doc(db, "users", player.userId);
          const playerSnap = await transaction.get(playerRef);
          const balance = Number(playerSnap.data()?.balance || 0);
          transaction.update(playerRef, {
            balance: Number((balance - betAmount).toFixed(2)),
            activeOmiRoomId: room.id,
          });
        }

        transaction.update(roomRef, {
          ...nextRoundState,
          updatedAt: serverTimestamp(),
          startedAt: serverTimestamp(),
        });
      });

      toast.success(iAmReady ? "Rematch vote removed." : "Rematch updated.");
    } catch (error) {
      toast.error(error?.message || "Failed to update rematch.");
    } finally {
      setBusy(false);
    }
  };

  const playingMessage =
    room.status === "finished"
      ? room.finishedReason === "forfeit"
        ? `${forfeitedPlayer?.username || "A player"} forfeited. ${
            room.winnerTeam === "teamA" ? "Seats 1 & 3" : "Seats 2 & 4"
          } won the table.`
        : room.winnerTeam === "draw"
          ? "Match drawn. All players refunded."
          : `${room.winnerTeam === "teamA" ? "Seats 1 & 3" : "Seats 2 & 4"} won the table.`
      : myTurn
        ? "Your turn to play a card."
        : `Waiting for ${currentTurnPlayer?.username || "the next player"}.`;

  return (
    <div className="w-full space-y-4">
      <div className="rounded-[30px] bg-[#161d28] p-5 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-emerald-300">Omi Match</div>
            <div className="mt-2 text-3xl font-black text-white">{room.roomId}</div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-full bg-[#1f2937] px-4 py-2 text-sm font-bold text-white">
              Trump: {formatSuit(room.trump)}
            </div>
            <div className="rounded-full bg-[#1f2937] px-4 py-2 text-sm font-bold text-emerald-300">
              Entry Fee: {Number(room.betAmount || 0).toFixed(2)} USDT
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {players.map((player) => (
            <div
              key={player.userId}
              className={`rounded-[20px] border px-4 py-4 ${
                room.turnUserId === player.userId
                  ? "border-emerald-400/50 bg-emerald-400/10"
                  : "border-white/10 bg-[#1d2531]"
              }`}
            >
              <div className="text-sm font-black text-white">
                {player.username}
                {player.userId === user?.uid ? " (You)" : ""}
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">
                Seat {player.seat}
              </div>
              <div className="mt-3 text-sm text-slate-300">
                Cards left: {(hands[player.userId] || []).length}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-[1.08fr_0.92fr]">
          <div className="order-1 rounded-[24px] bg-[#111827] p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Table</div>
              <div className="rounded-full bg-[#19212d] px-3 py-2 text-xs font-bold text-slate-300">
                Lead suit: {formatSuit(room.trick?.[0]?.card?.suit || room.lastTrick?.[0]?.card?.suit || room.trump)}
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {players.map((player) => {
                const currentCard = (room.trick || []).find((entry) => entry.userId === player.userId);
                const lastCard = (room.lastTrick || []).find((entry) => entry.userId === player.userId);
                const visibleCard = currentCard || lastCard;
                return (
                  <div
                    key={player.userId}
                    className={`rounded-[20px] border p-4 ${
                      currentCard
                        ? "border-emerald-400/30 bg-emerald-400/10"
                        : "border-white/8 bg-[#19212d]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-black text-white">
                          {player.username}
                          {player.userId === user?.uid ? " (You)" : ""}
                        </div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                          Seat {player.seat}
                        </div>
                      </div>
                      <div
                        className={`rounded-full px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] ${
                          room.turnUserId === player.userId && room.status === "playing"
                            ? "bg-emerald-500/15 text-emerald-300"
                            : "bg-white/5 text-slate-400"
                        }`}
                      >
                        {room.turnUserId === player.userId && room.status === "playing"
                          ? "Turn"
                          : currentCard
                            ? "Played"
                            : lastCard
                              ? "Last trick"
                              : "Waiting"}
                      </div>
                    </div>
                    <div className="mt-4 flex min-h-[92px] items-center justify-center rounded-[18px] bg-[#0f1722] px-4 py-5 text-center">
                      {visibleCard ? (
                        <div>
                          <div className="text-3xl font-black text-white">{visibleCard.card.label}</div>
                          <div className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-400">
                            {currentCard ? "Current trick" : "Previous trick"}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500">No card on table yet</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="order-2 rounded-[24px] bg-[#111827] p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Match Status</div>
            <div className="mt-4 space-y-3">
              <div className="rounded-[18px] bg-[#19212d] px-4 py-4 text-sm text-white">
                {playingMessage}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-[18px] bg-[#19212d] px-4 py-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Team A</div>
                  <div className="mt-2 text-2xl font-black text-emerald-300">
                    {Number(room.scores?.teamA || 0)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">Seats 1 & 3</div>
                </div>
                <div className="rounded-[18px] bg-[#19212d] px-4 py-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Team B</div>
                  <div className="mt-2 text-2xl font-black text-amber-300">
                    {Number(room.scores?.teamB || 0)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">Seats 2 & 4</div>
                </div>
              </div>
              <div className="text-xs text-slate-500">
                Trick {Number(room.trickNumber || 1)} of 8
              </div>
              {room.status === "finished" ? (
                <div className="rounded-[18px] bg-[#19212d] px-4 py-4 text-sm text-slate-300">
                  Rematch ready: <span className="font-black text-white">{rematchReady.length}</span> / {players.length}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-[30px] bg-[#161d28] p-5 shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
        <div className="text-xs uppercase tracking-[0.24em] text-slate-500">Your Hand</div>
        <div className="mt-2 text-sm text-slate-400">
          {myTurn && (room.trick || []).length
            ? "Follow the lead suit if you have it."
            : "Tap a card when it is your turn."}
        </div>
        <div className="mt-4 grid gap-3 grid-cols-2 md:grid-cols-4 2xl:grid-cols-8">
          {myHand.map((card) => (
            <button
              key={card.id}
              type="button"
              disabled={!myTurn || room.status !== "playing" || !playableHandIds.has(card.id)}
              onClick={() => handlePlayCard(card.id)}
              className={`rounded-[20px] border px-4 py-5 text-left transition hover:bg-[#19212d] disabled:opacity-50 ${
                playableHandIds.has(card.id)
                  ? "border-white/10 bg-[#111827] hover:border-emerald-400/40"
                  : "border-red-400/10 bg-[#0f1722]"
              }`}
            >
              <div className="text-3xl font-black text-white">{card.label}</div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                {card.suit}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          {room.status === "finished" ? (
            <button
              type="button"
              onClick={handleRematch}
              disabled={busy}
              className="rounded-[18px] bg-emerald-400 px-5 py-3 text-sm font-black text-[#05120d] disabled:opacity-60"
            >
              {busy
                ? "Updating..."
                : iAmReady
                  ? "Cancel Rematch Ready"
                  : "Ready For Rematch"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onLeave}
            className="rounded-[18px] bg-[#d64045] px-5 py-3 text-sm font-bold text-white"
          >
            {room.status === "finished" ? "Back to Omi Lobby" : "Forfeit Match"}
          </button>
        </div>
      </div>
    </div>
  );
}

function resolveNextRoundState(players, roundNumber) {
  const matchState = buildOmiMatchState(players);
  return {
    ...matchState,
    round: roundNumber + 1,
    rematchReady: [],
    finishedReason: null,
    forfeitedBy: null,
    winnerTeam: null,
    payoutProcessed: false,
  };
}
